import { forwardRef, useMemo } from 'react';
import { twMerge } from '@/lib/twMerge';
import UppbeatCredit from '@/components/Icons/UppbeatCredit';
import { useAccountModals } from '@/components/Modals/Account';
import { AccountModalNames, ModalConversionPoints } from '@/components/Modals/shared-types';
import Popover from '@/components/Popover';
import { Button } from '@music-vine/cadence/ui';
import { useIsLoggedIn, useSubscriptionType } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import { useSubscribeModal } from '@/hooks/billing/useSubscribeModal';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn, getFreeDownloadsText } from '@/lib/utils';
import { useCreditNudge } from './CreditNudge';
interface CreditIconProps {
  asButton?: boolean;
  className?: string;
  maxCredits: number | undefined;
  remainingCredits: number | undefined;
}
export const CreditIcon = forwardRef<HTMLButtonElement | HTMLDivElement, CreditIconProps>(({
  maxCredits,
  remainingCredits,
  className,
  asButton = false,
  ...props
}, ref) => {
  const Comp = asButton ? 'button' : 'div';
  return <Comp
  // mr instead of pr keeps the box (the tooltip anchor) tight to the
  // circle so the nudge card end-aligns on it, with identical spacing.
  // The usage badge compensates with ml-5 (was ml-1 + 16px padding).
  className={twMerge('relative mr-4 flex items-center pt-5 leading-none md:pt-0', asButton && 'group hover:brightness-95', className)} ref={ref as React.Ref<HTMLButtonElement & HTMLDivElement>} {...props}>
        <UppbeatCredit className="h-11 w-11 ring-pink-500 transition-all group-focus-visible:shadow-pink-500 group-focus-visible:ring-1 md:h-8 md:w-8" />
        <span className="absolute bottom-0 left-full ml-5 hidden -translate-x-full whitespace-nowrap text-nowrap rounded-full bg-gray-950 px-1 py-0 font-sans text-white text-xs tracking-wider md:block dark:bg-white dark:text-gray-950">
          {remainingCredits ?? 0}/{maxCredits ?? 0}
        </span>
      </Comp>;
});
const RemainingCredits = ({
  className
}: {
  className?: string;
}) => {
  const accountType = useSubscriptionType();
  const isLoggedIn = useIsLoggedIn();
  const {
    openModalWithConversionPoint
  } = useAccountModals();
  const {
    openSubscribeModalWithConversion
  } = useSubscribeModal();
  const handleClick = () => {
    if (isLoggedIn) {
      openSubscribeModalWithConversion(ModalConversionPoints.DownloadUpsell);
    } else {
      openModalWithConversionPoint(AccountModalNames.SignUp, ModalConversionPoints.DownloadUpsell);
    }
  };
  const {
    settings
  } = useSettings();
  const {
    isMd
  } = useTWScreens();
  const {
    nudgeTooltip
  } = useCreditNudge();
  const remainingCredits = settings?.credits?.credits_current;
  const maxCredits = settings?.credits?.credits_max;
  const renewal = settings?.user.renewed_download_date;
  const formattedRenewalDate = useMemo(() => {
    if (!renewal) {
      return null;
    }
    const date = new Date(renewal);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric'
    });
  }, [renewal]);
  if (accountType !== 'Free' || !isLoggedIn) {
    return null;
  }
  return <Popover.Container animateOverTrigger className="w-auto md:px-6 md:py-5" data-sentry-element="Popover.Container" data-sentry-component="RemainingCredits" data-sentry-source-file="RemainingCredits.tsx">
      <Popover.Trigger tooltip={isMd && nudgeTooltip ? nudgeTooltip : {
      label: 'Your free downloads'
    }} data-sentry-element="Popover.Trigger" data-sentry-source-file="RemainingCredits.tsx">
        <CreditIcon asButton className={cn('xxs:flex hidden', className)} maxCredits={maxCredits} remainingCredits={remainingCredits} data-sentry-element="CreditIcon" data-sentry-source-file="RemainingCredits.tsx" />
      </Popover.Trigger>
      <Popover.Content data-sentry-element="Popover.Content" data-sentry-source-file="RemainingCredits.tsx">
        <div className="flex flex-col gap-0">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col-reverse items-center justify-between gap-2 md:flex-row">
              <h3 className="font-semibold text-gray-950 text-lg 2xl:text-xl dark:text-white">
                Your free downloads
              </h3>
              <CreditIcon maxCredits={maxCredits} remainingCredits={remainingCredits} data-sentry-element="CreditIcon" data-sentry-source-file="RemainingCredits.tsx" />
            </div>
            <div className="flex flex-col gap-1">
              <p className="mt-[2px] text-center text-gray-700 text-sm md:text-left dark:text-gray-200">
                {getFreeDownloadsText(remainingCredits ?? 0)} remaining this
                month.
                <br />
                Next top-up:{' '}
                <span className="font-semibold">{formattedRenewalDate}</span>.
              </p>
            </div>
          </div>

          <Button className="mt-10 h-10 w-full text-sm md:mt-3 md:h-7 md:w-fit md:text-xs xl:h-8 xl:text-sm" fontSize={{
          default: 'sm',
          md: 'xs'
        }} onClick={handleClick} size={{
          default: 'sm',
          xl: 'lg'
        }} variant="brand" data-sentry-element="Button" data-sentry-source-file="RemainingCredits.tsx">
            <span className="hidden md:block">Subscribe for unlimited</span>
            <span className="block md:hidden">
              Subscribe for unlimited downloads
            </span>
          </Button>
        </div>
      </Popover.Content>
    </Popover.Container>;
};
export default RemainingCredits;